import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Col,
  Container,
  DropdownToggle,
  Row,
  Table,
  UncontrolledDropdown,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import Loading from '../../shared/components/custom/Loading';
import Api from '../../util/api';
import { partial } from '../../util/translation/translation';
import DotsHorizontalIcon from 'mdi-react/DotsHorizontalIcon';
import useModal from '../../shared/hooks/useModal';
import DeleteModal from '../../shared/components/custom/DeleteModal';
import { formatDateTime } from '../../util/date-and-time';
import { Link } from 'react-router-dom';

const REPLY_INDENT_PX = 20;

function getCommentAuthor(comment) {
  if (comment.employeeDetails) {
    const { employeeName, position } = comment.employeeDetails;
    return position
      ? `${employeeName} (${position})`
      : employeeName || comment.name || '-';
  }
  return (comment.name && comment.name.trim()) || '-';
}

function getCommentCompany(comment) {
  return comment.employeeDetails?.companyName || '-';
}

function flattenComments(comments, depth = 0) {
  const out = [];
  for (const comment of comments || []) {
    out.push({ comment, depth });
    if (comment.replies?.length) {
      out.push(...flattenComments(comment.replies, depth + 1));
    }
  }
  return out;
}

const CommentsListPage = ({ entityId, entityType }) => {
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(true);
  const [handleModal, visible, deleteID] = useModal();

  const p = partial('comments');
  const s = partial('shared');

  const fetchComments = async () => {
    if (!entityId) return;
    setLoadingComments(true);
    try {
      const resComments = await Api.comments.getCommentsForEntity(
        entityId,
        entityType
      );
      setComments(resComments || []);
    } catch (err) {
      toast.error(err || p('errorLoadingComments'));
    } finally {
      setLoadingComments(false);
    }
  };
  useEffect(() => {
    fetchComments();
  }, []);

  const deleteComment = async () => {
    try {
      await Api.comments.deleteComment(deleteID);
      toast.success(p('commentDeleted'));
    } catch {
      toast.error(p('errorDeletingComment'));
    } finally {
      handleModal();
      fetchComments();
    }
  };

  const renderComments = () => {
    return flattenComments(comments).map(({ comment, depth }, i) => (
      <tr
        style={{
          background: `rgba(0, 0, 0,${(depth * 3) / 100})`,
          borderBottom: '1px solid #eee',
        }}
        key={`comment-${comment.id}`}
      >
        <td>
          <div
            style={{
              paddingLeft: depth * REPLY_INDENT_PX,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            <span
              style={{
                fontSize: 10,
                display: 'block',
                color: '#6B7280',
                marginRight: 8,
              }}
            >
              {formatDateTime(comment.createdAt)} -{' '}
              <Link
                to={`/users/edit/${comment.userId}`}
                style={{ fontSize: 12, fontWeight: 500 }}
              >
                {comment?.employeeDetails?.employeeName || comment.name}
              </Link>{' '}
              -{' '}
              {comment?.employeeDetails?.position &&
                comment?.employeeDetails?.position + '- '}
              {comment?.employeeDetails?.companyId && (
                <Link
                  to={`/companies/edit/${comment?.employeeDetails?.companyId}?tab=1`}
                  style={{ fontSize: 12, fontWeight: 500 }}
                >
                  {getCommentCompany(comment)}
                </Link>
              )}
            </span>
            <span style={{ display: 'block' }}>{comment.text}</span>
            {comment.reactions?.length > 0 && (
              <span
                style={{
                  padding: '4px 13px 4px 6px',
                  backgroundColor: '#eee',
                  borderRadius: 10,
                }}
              >
                {comment.reactions.map((r, j) => (
                  <span key={j} title={r.userId} style={{ marginRight: -6 }}>
                    {r.emoji}
                  </span>
                ))}
              </span>
            )}
          </div>
        </td>
        <td>
          <UncontrolledDropdown className="dashboard__table-more">
            <DropdownToggle>
              <DotsHorizontalIcon />
            </DropdownToggle>
            <DropdownMenu className="dropdown__menu">
              <DropdownItem
                onClick={() => handleModal(comment.id)}
                className="danger"
              >
                {s('delete')}
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        </td>
      </tr>
    ));
  };

  return (
    <Container>
      <DeleteModal
        visible={visible}
        type={p('comment')}
        handleModal={handleModal}
        modalAction={deleteComment}
      />
      <Loading loading={loadingComments} />
      <Row>
        <Col>
          <div
            style={{
              display: 'flex',
              marginTop: 10,
              marginBottom: 10,
              background: '#fff',
              borderRadius: 6,
              border: '1px solid rgba(0,0,0,0.08)',
            }}
          >
            <Table responsive striped>
              <thead>
                <tr>
                  <th style={{ color: '#4D576A' }}>
                    <div className="flex alignItems-center">{p('comment')}</div>
                  </th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {comments.length === 0 && !loadingComments ? (
                  <tr>
                    <td
                      colSpan={5}
                      style={{
                        color: '#6B7280',
                        textAlign: 'center',
                        padding: 24,
                      }}
                    >
                      {p('noComments')}
                    </td>
                  </tr>
                ) : (
                  renderComments()
                )}
              </tbody>
            </Table>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default CommentsListPage;
